import type { PopoverContentProps } from '@radix-ui/react-popover';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { DayPicker, DayPickerBase } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

import { OmitStrict } from '../../types';
import { cn } from '../../utils';
import Dropdown from '../Dropdown/Dropdown';

import {
  DATE_PICKER_FORMAT,
  DATE_PICKER_EARLIEST_DATE,
  DatePickerClassNames,
  DAY_PICKER_MONDAY_INDEX,
  DAY_PICKER_SATURDAY_INDEX,
  DAY_PICKER_SUNDAY_INDEX,
} from './constants';
import {
  CalendarContainer,
  Caption,
  DatePickerPopoverContentRoot,
} from './DatePicker.styled';
import { useMonthOptions, useYearOptions } from './hooks';
import { generateDateRange, getCurrentYear, isValidDate } from './utils';

export type DatePickerPopoverContentProps = OmitStrict<
  PopoverContentProps,
  'onChange' | 'children'
> & {
  value: string;
  onChange: (date: string) => void;
  onDayBlur?: (event: React.FocusEvent | React.KeyboardEvent) => void;
  onDayEscape?: (event: React.KeyboardEvent<Element>) => void;
  minDate?: string;
  maxDate?: string;
  dayPickerProps?: DayPickerBase;
  enabledDays?: string[];
  disabledDays?: string[];
  /** automatically focuses to element when opened */
  autoFocus?: boolean;
};

const DatePickerPopoverContent = forwardRef<
  HTMLDivElement,
  DatePickerPopoverContentProps
>((props, forwardedRef) => {
  const {
    value,
    onChange,
    onDayBlur,
    onDayEscape,
    minDate: passedMinDate,
    maxDate: passedMaxDate,
    dayPickerProps,
    enabledDays,
    disabledDays: passedDisabledDays = [],
    className,
    autoFocus = false,
    ...rest
  } = props;

  const minDate = isValidDate(passedMinDate)
    ? passedMinDate
    : DATE_PICKER_EARLIEST_DATE;

  const maxDate = isValidDate(passedMaxDate)
    ? passedMaxDate
    : `${getCurrentYear() + 5}/01/01`;

  // filters for day picker
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [date, setDate] = useState(value);

  useEffect(() => {
    setMonth(`${dayjs(value).month()}`);
    setYear(`${dayjs(value).year()}`);
    setDate(value);
  }, [value]);

  const monthOptions = useMonthOptions({ minDate, maxDate, date });

  const yearOptions = useYearOptions({ minDate, maxDate });

  const disabledDays = useMemo(() => {
    if (!enabledDays) return passedDisabledDays;

    const dates = generateDateRange(dayjs(minDate), dayjs(maxDate));

    return [
      ...dates.filter(x => !enabledDays.includes(x)),
      ...passedDisabledDays,
    ];
  }, [enabledDays, maxDate, minDate, passedDisabledDays]);

  return (
    <DatePickerPopoverContentRoot
      className={cn(DatePickerClassNames.PopoverContent, className)}
      collisionPadding={8}
      sideOffset={8}
      onOpenAutoFocus={event => {
        if (!autoFocus) {
          event.preventDefault();
        }
      }}
      {...rest}
      ref={forwardedRef}>
      <CalendarContainer>
        <Caption>
          <Dropdown
            inputProps={{
              label: 'Month',
              isLabelHidden: true,
            }}
            options={monthOptions}
            value={month}
            onChange={newValues => {
              const newValue = Array.isArray(newValues)
                ? newValues[0]
                : newValues;

              if (typeof newValue === 'undefined') return;

              setDate(dayjs(date).month(+newValue).format(DATE_PICKER_FORMAT));

              setMonth(newValue);
            }}
          />
          <Dropdown
            inputProps={{
              label: 'Year',
              isLabelHidden: true,
            }}
            options={yearOptions}
            value={year}
            onChange={newValues => {
              const newValue = Array.isArray(newValues)
                ? newValues[0]
                : newValues;

              if (typeof newValue === 'undefined') return;

              setDate(dayjs(date).year(+newValue).format(DATE_PICKER_FORMAT));

              setYear(newValue);
            }}
          />
        </Caption>

        <DayPicker
          {...dayPickerProps}
          disabled={[
            {
              after: dayjs(maxDate).toDate(),
              before: dayjs(minDate).toDate(),
            },
            {
              dayOfWeek: [DAY_PICKER_SUNDAY_INDEX, DAY_PICKER_SATURDAY_INDEX],
            },
            ...disabledDays.map(item => new Date(item)),
          ]}
          month={dayjs(date).toDate()}
          selected={[dayjs(value).toDate()]}
          weekStartsOn={DAY_PICKER_MONDAY_INDEX}
          onDayBlur={(_, __, event) => {
            onDayBlur?.(event);
          }}
          onDayClick={newDate => {
            onChange(dayjs(newDate).format(DATE_PICKER_FORMAT));
          }}
          onDayKeyDown={(_, __, event) => {
            if (event.key === 'Escape') {
              onDayEscape?.(event);
            }
          }}
        />
      </CalendarContainer>
    </DatePickerPopoverContentRoot>
  );
});

DatePickerPopoverContent.displayName = 'DatePickerPopoverContent';

export default DatePickerPopoverContent;
